import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import { reviewPatientRecord, trackContextualAIEvent } from "../../services/contextualAiApi";


export default function PatientRecordAIReviewCard({ patientId, onClose }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tracked = useRef(false);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackContextualAIEvent("ai_context_action_shown", {
      module: "patient_record",
      action: "structured_summary",
      metadata: { context_type: "patient_record" },
    });
  }, []);

  const runReview = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    const startedAt = Date.now();
    trackContextualAIEvent("ai_context_action_clicked", {
      module: "patient_record",
      action: "structured_summary",
      metadata: { context_type: "patient_record" },
    });
    try {
      const result = await reviewPatientRecord(patientId);
      setAnalysis(result);
      trackContextualAIEvent("ai_context_generation_success", {
        module: "patient_record",
        action: "structured_summary",
        outcome: "rules_only",
        duration_ms: Date.now() - startedAt,
        analysis_id: result?.id,
        metadata: { context_type: "patient_record", status: result?.status },
      });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Não foi possível gerar o resumo do prontuário agora.");
      trackContextualAIEvent("ai_context_generation_failed", {
        module: "patient_record",
        action: "structured_summary",
        outcome: "error",
        duration_ms: Date.now() - startedAt,
        metadata: { context_type: "patient_record", error_code: String(requestError?.response?.status || "request_failed") },
      });
    } finally {
      setLoading(false);
    }
  };

  if (!analysis) {
    return (
      <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-purple-700" />
            <div>
              <p className="font-semibold text-gray-900">Veja o prontuário de forma organizada</p>
              <p className="mt-1 text-sm text-gray-600">Reúna fatos da anamnese, evoluções, odontograma, tratamentos e documentos sem alterar os registros.</p>
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border bg-white px-3 py-2 text-sm">Fechar</button>
            <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Gerar resumo
            </button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </div>
    );
  }

  const payload = analysis.payload || {};
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const missingSections = Array.isArray(payload.missing_sections) ? payload.missing_sections : [];
  const generatedAt = payload.generated_snapshot_at ? new Date(payload.generated_snapshot_at).toLocaleString("pt-BR") : "";

  return (
    <div className="rounded-xl border border-purple-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-700" />
          <div>
            <p className="font-semibold text-gray-900">Resumo assistido do prontuário</p>
            <p className="text-xs text-gray-500">Fotografia gerada em {generatedAt}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={runReview} disabled={loading} className="inline-flex items-center gap-1 text-xs font-medium text-purple-700"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Atualizar fotografia</button>
          <button type="button" onClick={onClose} aria-label="Fechar"><X className="h-4 w-4 text-gray-500" /></button>
        </div>
      </div>

      <p className="mt-3 text-sm text-gray-700">{payload.summary}</p>
      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {sections.map((section) => (
          <div key={section.key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-sm font-semibold text-gray-900">{section.title}</p>
            <p className="mt-0.5 text-xs text-gray-500">Fonte: {section.source}</p>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              {(section.items || []).map((item, index) => <li key={index}>• {item}</li>)}
            </ul>
          </div>
        ))}
      </div>

      {missingSections.length > 0 && (
        <div className="mt-4 flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Sem registros disponíveis nesta fotografia: {missingSections.join(", ")}.</span>
        </div>
      )}
      <p className="mt-4 text-xs text-gray-500">{payload.disclaimer}</p>
      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </div>
  );
}
